import { h, JSX } from 'preact';
import ObservingComponent from '../componentBases/observingComponent';
import Button from '../buttons/button';
import FormattedDate from '../text/formattedDate';
import { DateUtils, InteractiveVenueAssignerMeeting, MeetingStatuses, MeetingSchedule as FrontendMeetingSchedule } from 'core.frontend';
import MeetingDimension from '../events/reports/meetingDimension';
import Field from '../fields/field';
import FormattedTimespan from '../text/formattedTimespan';

export interface MeetingScheduleProps {
    meetingSchedule: FrontendMeetingSchedule;
}

class MeetingSchedule extends ObservingComponent<MeetingScheduleProps> {

    private readonly rowHeight: number = 48;
    private readonly columnMinWidth: number = 150;
    private readonly minMeetingHeight: number = 20;

    public componentWillMount(): void {
        this.registerUpdateObserver(this.props.meetingSchedule.observationProvider);
    }

    private getFirstGridDateTime(dateStr: string) {
        const { meetingSchedule } = this.props;

        var dateParts = dateStr.split('-');
        dateParts[2] = dateParts[2].split('T')[0];

        var year = dateParts[0];
        var monthStr = dateParts[1];
        var dayStr = dateParts[2];

        if (monthStr.length < 2) {
            monthStr = '0' + monthStr;
        }

        if (dayStr.length < 2) {
            dayStr = '0' + dayStr;
        }

        const firstDateTime = new Date(`${year}-${monthStr}-${dayStr}T${meetingSchedule.timeSlots[0].substring(0,2)}:${meetingSchedule.timeSlots[0].substring(2)}`);

        return firstDateTime;
    }

    private getMeetingOffsetMinutes = (startDateTime: Date) => {
        /* calculates the number of minutes a meeting start time is before the first time on the grid */
        var offset = DateUtils.getMinutesDifference(this.getFirstGridDateTime(startDateTime.toString()), startDateTime);
        return offset > 0 ? offset : 0;
    }

    private getMeetingBlockHeight = (meeting: InteractiveVenueAssignerMeeting) => {
        let startTime = meeting.startDateTime;
        let endTime = meeting.endDateTime;
        
        // If pre-meeting pickup is available and there's a pickup time, use that instead
        if (meeting.preMeetingPickupAvailable && meeting.preMeetingPickupTime) {
            const pickupResult = this.getPickupDateTime(meeting.startDateTime, meeting.preMeetingPickupTime);
            if (pickupResult && pickupResult.date < new Date(meeting.startDateTime)) {
                startTime = pickupResult.isoString as unknown as Date;
            }
        }
        
        // If post-meeting pickup is available and there's a pickup time, extend the end time
        if (meeting.postMeetingPickupAvailable && meeting.postMeetingPickupTime) {
            const pickupResult = this.getPickupDateTime(meeting.endDateTime, meeting.postMeetingPickupTime);
            if (pickupResult && pickupResult.date > new Date(meeting.endDateTime)) {
                endTime = pickupResult.isoString as unknown as Date;
            }
        }
        
        let lengthInMinutes = DateUtils.getMinutesDifference(endTime, startTime);
        lengthInMinutes -= this.getMeetingOffsetMinutes(startTime);

        const exactHeight = (lengthInMinutes / 30 * this.rowHeight) - 1;

        const heightTrimmedAtBottom = Math.min(
            exactHeight - 2,
            (this.rowHeight * this.props.meetingSchedule.timeSlots.length) - exactHeight - 2
        )

        return Math.max(this.minMeetingHeight, heightTrimmedAtBottom);
    }

    private getMeetingBlockTop = (meeting: InteractiveVenueAssignerMeeting) => {
        let startTime = meeting.startDateTime;
    
        // If pre-meeting pickup is available and there's a pickup time, use that instead
        if (meeting.preMeetingPickupAvailable && meeting.preMeetingPickupTime) {
            const pickupResult = this.getPickupDateTime(meeting.startDateTime, meeting.preMeetingPickupTime);
            
            if (pickupResult && pickupResult.date < new Date(meeting.startDateTime)) {
                startTime = pickupResult.isoString as unknown as Date;
            }
        }
        
        let minutesFromBase = DateUtils.getMinutesDifference(startTime, this.getFirstGridDateTime(startTime.toString()));
        minutesFromBase += this.getMeetingOffsetMinutes(startTime);

        return minutesFromBase / 30 * this.rowHeight;
    }

    private getMeetingDimensions = () => {
        const { meetingSchedule } = this.props;

        const meetingDimensions: MeetingDimension[] = [];

        for(let j = 0; j < meetingSchedule.meetings.length; j++) {

            const meeting = meetingSchedule.meetings[j];

            const top = this.getMeetingBlockTop(meeting) + 32; // 2rem headeroffset
            const height =  this.getMeetingBlockHeight(meeting);

            meetingDimensions.push({
                top: top,
                height: height,
                left: 0,
                right: 0,
                bottom: top + height,
                rightAdjacentMeetings: 0,
                column: 0,
                meetingId: meeting.id
            });
        }

        // sort by top then by bottom
        const sortedMeetingDimensions = meetingDimensions.sort((a, b) => {
            if (a.top < b.top) {
                return -1;
            }

            if (a.top > b.top) {
                return 1;
            }

            if (a.bottom > b.bottom) {
                return -1;
            }

            if (a.bottom < b.bottom) {
                return 1;
            }

            return 0;
        });

        this.setMeetingDimensionsColumnNumber(sortedMeetingDimensions, 0);

        return sortedMeetingDimensions;
    }

    private setMeetingDimensionsColumnNumber(meetingDimensions: MeetingDimension[], seedColumnNumber: number) {

        var lastMeetingBottom = null;
        var columns = [];

        for (let i = 0; i < meetingDimensions.length; i++) {
             const meetingDimension = meetingDimensions[i];

            if (lastMeetingBottom !== null && meetingDimension.top >= lastMeetingBottom) {
                this.setColumnNumber(columns);
                columns = [];
                lastMeetingBottom = null;
            }

            var placed = false;

            for (var j = 0; j < columns.length; j++) {
                var col = columns[j];

                if (
                    !(
                        col[col.length-1].bottom > meetingDimension.top &&
                        col[col.length-1].top < meetingDimension.bottom
                    )
                ) {
                    col.push(meetingDimension);
                    placed = true;
                    break;
                }
            }

            if (!placed) {
                columns.push([meetingDimension]);
            }

            if (lastMeetingBottom === null || meetingDimension.bottom > lastMeetingBottom) {
                lastMeetingBottom = meetingDimension.bottom;
            }
        }

        if (columns.length > 0) {
            this.setColumnNumber(columns);
        }
    }

    private setColumnNumber(columns: MeetingDimension[][]) {
        var columnCount = columns.length;

        for (var i = 0; i < columnCount; i++) {
            var col = columns[i];

            for(var j = 0; j < col.length; j++) {
                const meetingDimension = col[j];
                meetingDimension.column = i + 1;
                meetingDimension.rightAdjacentMeetings = columnCount - meetingDimension.column;
                meetingDimension.leftPercent = `${((meetingDimension.column - 1) / columnCount) * 100}%`;
                meetingDimension.widthPercent = `${1 / columnCount * 100}%`;
            }
        }
    }

    private getColumnWidth(meetingDimensions: MeetingDimension[]) {
        return this.columnMinWidth * Math.max(...meetingDimensions.map((dimension) => dimension.column));
    }

    private buildMeetingStyle = (meeting: InteractiveVenueAssignerMeeting, meetingDimensions: MeetingDimension[], columnWidth: number) => {
        const meetingDimension = meetingDimensions.find((d) => d.meetingId === meeting.id);

        if (!meetingDimension) {
            return;
        }

        return {
            top: meetingDimension.top + 1,
            height: meetingDimension.height,
            left: meetingDimension.leftPercent,
            width: meetingDimension.widthPercent
        };
    }

    private meetingClicked = (meeting: InteractiveVenueAssignerMeeting) => (e: MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();

        this.props.meetingSchedule.meetingClicked(meeting);
    }

    private getPickupDateTime(baseDate: string | Date, pickupTime: string): { date: Date, isoString: string } | null {

        if (!pickupTime) return null;
    
        const parts = pickupTime.split(':');
        if (parts.length < 2) return null;
        
        const hours = parseInt(parts[0]);
        const minutes = parseInt(parts[1]);
        const seconds = parts.length > 2 ? parseInt(parts[2]) : 0;
        
        if (isNaN(hours) || isNaN(minutes)) return null;
        
        // Convert baseDate to string if it's a Date object
        const baseDateStr = typeof baseDate === 'string' ? baseDate : baseDate.toString();
        
        // Extract timezone offset from the base date
        const timezoneMatch = baseDateStr.match(/([+-]\d{2}:\d{2})/);
        const timezoneOffset = timezoneMatch ? timezoneMatch[1] : 'Z';
        
        // Extract the date part (YYYY-MM-DD)
        const datePart = baseDateStr.substring(0, 10);
        
        // Create a new date string with the pickup time and the original timezone
        const newDateStr = `${datePart}T${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}${timezoneOffset}`;
        
        return {
            date: new Date(newDateStr),
            isoString: newDateStr
        };
    }

    private getMeetingSegments(meeting: InteractiveVenueAssignerMeeting) {
        const segments = [];
        let actualStartTime = meeting.startDateTime;
        let actualEndTime = meeting.endDateTime;
        
        // Check for pre-meeting pickup
        if (meeting.preMeetingPickupAvailable && meeting.preMeetingPickupTime) {
            const pickupResult = this.getPickupDateTime(meeting.startDateTime, meeting.preMeetingPickupTime);
            
            if (pickupResult && pickupResult.date < new Date(meeting.startDateTime)) {
                // Add pre-meeting pickup segment
                segments.push({
                    type: 'pre-pickup',
                    startTime: pickupResult.isoString,
                    endTime: meeting.startDateTime,
                });
            }
        }
        
        // Add main meeting segment
        segments.push({
            type: 'meeting',
            startTime: actualStartTime,
            endTime: actualEndTime,
        });
        
        // Check for post-meeting pickup
        if (meeting.postMeetingPickupAvailable && meeting.postMeetingPickupTime) {
            const pickupResult = this.getPickupDateTime(meeting.endDateTime, meeting.postMeetingPickupTime);
            
            if (pickupResult && pickupResult.date > new Date(meeting.endDateTime)) {
                // Add post-meeting pickup segment
                segments.push({
                    type: 'post-pickup',
                    startTime: meeting.endDateTime,
                    endTime: pickupResult.isoString,
                });
            }
        }
        
        return segments;
    }

    private buildMeeting = (meeting: InteractiveVenueAssignerMeeting, meetingDimensions: MeetingDimension[], columnWidth: number) => {
        const baseStyle = this.buildMeetingStyle(meeting, meetingDimensions, columnWidth);
        
        if (!baseStyle) { 
            return null;
        }
        
        // Segements are pre-pickup, meeting and post-pickup
        const segments = this.getMeetingSegments(meeting);
        
        if (segments.length === 1) {
            // Just render a single block for meetings without pickup times
            return (
                <div
                    class={'cursor-default meeting ' + (meeting.status == MeetingStatuses.Working ? ' working ' : '')}
                    style={{
                        top: baseStyle.top,
                        left: baseStyle.left,
                        width: baseStyle.width,
                        height: baseStyle.height,
                        margin: '1px',
                        cursor: 'pointer !important',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        borderRight: baseStyle.width !== '100%' ? '2px solid #fff' : 'none'
                    }}
                    id={'meeting_' + meeting.id}
                    onClick={this.meetingClicked(meeting)}>

                    <div class="meeting-segment">
                        <div class="meeting-label">
                            #{meeting.meetingNumber} | {meeting.name} | {meeting.venue}
                            {!!meeting.assignedTables && <span> | {meeting.assignedTables}</span>}
                        </div>
                    </div>
                </div>
            );
        }
        
        // For meetings with pickup times, render multiple segments
        return (
            <div
                class={'cursor-default meeting-container'}
                style={{
                    position: 'absolute',
                    top: baseStyle.top,
                    left: baseStyle.left,
                    width: baseStyle.width,
                    height: baseStyle.height,
                    margin: '1px',
                    cursor: 'pointer !important',
                    borderRight: baseStyle.width !== '100%' ? '2px solid #fff' : 'none'
                }}
                id={'meeting_container_' + meeting.id}
                onClick={this.meetingClicked(meeting)}>
                
                {segments.map((segment, index) => {
                    // Calculate segment dimensions
                    const segmentTop = index === 0 ? 0 : 
                        DateUtils.getMinutesDifference(segment.startTime, segments[0].startTime) / 30 * this.rowHeight;
                    const segmentHeight = DateUtils.getMinutesDifference(segment.endTime, segment.startTime) / 30 * this.rowHeight;
                    
                    return (
                        <div
                            class={`meeting-segment ${segment.type} ${meeting.status == MeetingStatuses.Working ? 'working' : ''}`}
                            style={{
                                position: 'absolute',
                                top: `${segmentTop}px`,
                                left: '0',
                                width: '100%',
                                height: `${segmentHeight}px`,
                                backgroundColor: segment.type === 'pre-pickup' ? '#b9e4f6' : 
                                                 segment.type === 'post-pickup' ? '#b9e4f6' : 
                                                 '#005377',
                                margin: '0',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center',
                            }}>
                            {segment.type === 'meeting' && (
                                <div class="meeting-label">
                                    #{meeting.meetingNumber} | {meeting.name} | {meeting.venue}
                                    {!!meeting.assignedTables && <span> | {meeting.assignedTables}</span>}
                                </div>
                            )}
                            {segment.type === 'pre-pickup' && (
                                <div class="pickup-label">
                                    Before Meeting Pickup: <FormattedTimespan timespan={meeting.preMeetingPickupTime}/> | Pickup: {meeting.preMeetingPickupLocation} | Drop-off: {meeting.preMeetingDropOffLocation}
                                    {!!meeting.preMeetingPickupComments && <span> | Comments: {meeting.preMeetingPickupComments}</span>}
                                </div>
                            )}
                            {segment.type === 'post-pickup' && (
                                <div class="pickup-label">
                                    After Meeting Pickup: <FormattedTimespan timespan={meeting.postMeetingPickupTime}/> | Pickup: {meeting.postMeetingPickupLocation} Drop-off: {meeting.postMeetingDropOffLocation}
                                    {!!meeting.postMeetingPickupComments && <span> | Comments: {meeting.postMeetingPickupComments}</span>}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        );
    }

    private buildMeetings = (meetingDimensions: MeetingDimension[], columnWidth: number) => {
        return this.props.meetingSchedule.meetings.map((meeting) => {
            return this.buildMeeting(meeting, meetingDimensions, columnWidth);
        });
    }

    private buildSchedule() {
        const { meetingSchedule } = this.props;
        const meetingDimensions = this.getMeetingDimensions();
        const columnWidth = this.getColumnWidth(meetingDimensions);

        return (
            <div class="mt-1">
                <div class="pl-1 flex text-lg">

                    <Button
                        className="v-center ml-auto mr-1 btn-icon"
                        button={meetingSchedule.previousDayButton} />

                    <FormattedDate
                        className="v-center"
                        date={meetingSchedule.selectedDate} />

                    <Button
                        className="v-center mr-auto ml-1 btn-icon"
                        button={meetingSchedule.nextDayButton} />
                </div>

                <div class="flex mt-p5">
                    <div class="time-column">
                        <div class="header"></div>

                        {meetingSchedule.timeSlots.map((timeSlot) => (
                            <div class="time-slot">
                                <span>{timeSlot}</span>
                            </div>
                        ))}
                    </div>

                    <div class="venue">

                        <div class='header'>
                            <span>My Meeting Schedule</span>
                        </div>

                        {meetingSchedule.timeSlots.map(() => (
                            <div class="time-slot"></div>
                        ))}

                        {this.buildMeetings(meetingDimensions, columnWidth)}
                    </div>
                </div>
            </div>
        );
    }

    public render({ meetingSchedule }: MeetingScheduleProps): JSX.Element {
        return (
            <div class="interactive-room-assigner">

                <div class="fill mt-1">
                    <Field
                        className="no-label"
                        field={meetingSchedule.attendeeDropdown} />
                </div>

                {!meetingSchedule.isLoading &&
                    this.buildSchedule()
                }

                {!!meetingSchedule.isLoading &&
                    <div>
                        <div className="loading-placeholder shimmer"></div>
                        <div className="loading-placeholder shimmer"></div>
                        <div className="loading-placeholder shimmer"></div>
                        <div className="loading-placeholder shimmer"></div>
                        <div className="loading-placeholder shimmer"></div>
                        <div className="loading-placeholder shimmer"></div>
                        <div className="loading-placeholder shimmer"></div>
                        <div className="loading-placeholder shimmer"></div>
                        <div className="loading-placeholder shimmer"></div>
                        <div className="loading-placeholder shimmer"></div>
                        <div className="loading-placeholder shimmer"></div>
                    </div>
                }
            </div>
        );
    }
}

export default MeetingSchedule;